import { AgreementReturnModel } from "../../models/agreement";
import { createRef, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faFilter,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Location as TQSLocation } from "../../models/location";
import "../../stylesheet/sidebar.css";
import { LocationCard } from "./location-card";
import { Modal } from "../../components/ui/modal";
import { useSearchParams } from "react-router-dom";
import { environment } from "../../environments/environment";
import "react-tooltip/dist/react-tooltip.css";
import { Tooltip } from "react-tooltip";
import { useModal } from "../../components/hooks/useModal";
import { getQualityAlertsByLocations } from "../../reducers/qualityAlertSlice";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { 
  getAgreementsBySupplier, 
  selectSelectedSupplier, 
  selectSelectedProducingFacilities 
} from "../../reducers/supplierSlice";
import { processAgreements, selectFilteredLocations, selectLocations, selectOem, selectSelectedOems, setSelectedOems, setSearch } from "../../reducers/locationSlice";

const modalIdentifier = "serviceLocationFilterModal";

export function ServiceLocations(props: {
  selectedLocation: TQSLocation;
  setSelectedLocation: (location: TQSLocation) => void;
  setAllLocations: (locations: TQSLocation[]) => void;
}) {
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const modalRef = useRef<any>();
  const dispatch = useAppDispatch();
  const selectedSupplier = useAppSelector(selectSelectedSupplier);
  const selectedProducingFacilities = useAppSelector(selectSelectedProducingFacilities);
  const filteredLocations = useAppSelector(selectFilteredLocations);
  const locations = useAppSelector(selectLocations);
  const oem = useAppSelector(selectOem);
  const selectedOems = useAppSelector(selectSelectedOems);
  // const [filteredLocations, setFilteredLocations] = useState<TQSLocation[]>([]);
  // const [locations, setLocations] = useState<TQSLocation[]>([]);
  // const [oem, setOem] = useState<{ id: number; name: string }[]>([]);
  // const [selectedOems, setSelectedOems] = useState<{ id: number; name: string }[]>([]);
  const [search, setSearchInput] = useState<string>("");

  const [open, setOpen, isOpen] = useModal();

  // Load and process data when supplier or producing facilities change
  useEffect(() => {
    const loadData = async () => {
      if (!selectedSupplier?.id) return;

      // Clear URL parameters when supplier changes
      searchParams.delete('oems');
      setSearchParams(searchParams);

      // setisLoading(true);
      try {
        var agreementsResult = undefined;
        // Only fetch agreements if supplier changes
        if (!locations.length) {
          agreementsResult = await dispatch(getAgreementsBySupplier(selectedSupplier.id));
          if (agreementsResult.type.endsWith('/rejected')) {
            throw new Error('Failed to fetch agreements');
          }
        }
        
        const processResult = await dispatch(processAgreements(agreementsResult?.payload as AgreementReturnModel[]));
        if (processResult.type.endsWith('/rejected')) {
          throw new Error('Failed to process agreements');
        }
        
        if (!processResult.payload) {
          throw new Error('No locations data received');
        }
        
        // Create deep copies of the result to avoid modification issues
        const locationsCopy = JSON.parse(JSON.stringify(processResult.payload));
        props.setAllLocations(locationsCopy);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setisLoading(false);
      }
    };

    setTimeout(() => {
      loadData();
    }, 100);
  }, [dispatch, selectedSupplier?.id]);

  useEffect(() => {
    searchLocations(search);
  }, [selectedOems, search]);

  // // Add monitoring effect for filteredLocations
  // useEffect(() => {
  //   console.log('filteredLocations updated:', {
  //     count: filteredLocations.length,
  //     oemCount: selectedOems.length,
  //     timestamp: new Date().toISOString()
  //   });
  // }, [filteredLocations, selectedOems]);

  // Load OEM filters from URL when locations are processed
  useEffect(() => {
    if (oem.length > 0) {
      const oemParam = searchParams.get('oems');
      if (oemParam) {
        try {
          const selectedOemIds = oemParam.split(',').map(Number);
          const filteredOems = oem.filter(o => selectedOemIds.includes(o.id));
          if (filteredOems.length > 0) {
            dispatch(setSelectedOems(filteredOems));
          }
        } catch (error) {
          console.error('Error parsing OEM params:', error);
        }
      }
    }
  }, [oem, searchParams]);

  const resetFilter = () => {
    dispatch(setSelectedOems(oem));
    setSearchInput("");
    dispatch(setSearch(""));
    setOpen("");
    
    // Reset allLocations to the original unfiltered locations
    const resetLocs = locations.map(location => {
      // Create a deep copy of the location and its nested objects
      const locationCopy = JSON.parse(JSON.stringify(location));
      // Ensure agreement and its properties are properly copied
      if (locationCopy.agreement) {
        locationCopy.agreement = {
          ...JSON.parse(JSON.stringify(locationCopy.agreement)),
          assignedReps: locationCopy.agreement.assignedReps?.map((rep: { representative: any }) => ({
            ...rep,
            representative: rep.representative ? { ...rep.representative } : null
          })) || []
        };
      }
      return locationCopy;
    });
    
    props.setAllLocations(resetLocs);
    
    // Clear OEM filter from URL
    searchParams.delete('oems');
    setSearchParams(searchParams);
  };

  const updateSelectedOems = (
    e: React.ChangeEvent<HTMLInputElement>,
    selectedOem: { id: number; name: string },
  ) => {
    const newSelectedOems = e.target.checked 
      ? [...selectedOems, selectedOem]
      : selectedOems.filter((o) => o.id !== selectedOem.id);
    
    dispatch(setSelectedOems(newSelectedOems));
    
    // Update URL with selected OEM IDs
    if (newSelectedOems.length === 0 || newSelectedOems.length === oem.length) {
      searchParams.delete('oems');
    } else {
      searchParams.set('oems', newSelectedOems.map(o => o.id).join(','));
    }
    setSearchParams(searchParams);
    
    // Update allLocations based on OEM filter
    // If no OEMs are selected, show all locations
    const filteredLocs = newSelectedOems.length === 0
      ? locations.map(location => {
          // Create a deep copy of the location and its nested objects
          const locationCopy = JSON.parse(JSON.stringify(location));
          // Ensure agreement and its properties are properly copied
          if (locationCopy.agreement) {
            locationCopy.agreement = {
              ...JSON.parse(JSON.stringify(locationCopy.agreement)),
              assignedReps: locationCopy.agreement.assignedReps?.map((rep: { representative: any }) => ({
                ...rep,
                representative: rep.representative ? { ...rep.representative } : null
              })) || []
            };
          }
          return locationCopy;
        })
      : locations
          .filter((l) => newSelectedOems.map((o) => o.id).includes(l.oem?.id ?? 19))
          .map(location => {
            // Create a deep copy of the location and its nested objects
            const locationCopy = JSON.parse(JSON.stringify(location));
            // Ensure agreement and its properties are properly copied
            if (locationCopy.agreement) {
              locationCopy.agreement = {
                ...JSON.parse(JSON.stringify(locationCopy.agreement)),
                assignedReps: locationCopy.agreement.assignedReps?.map((rep: { representative: any }) => ({
                  ...rep,
                  representative: rep.representative ? { ...rep.representative } : null
                })) || []
              };
            }
            return locationCopy;
          });
    
    props.setAllLocations(filteredLocs);
  };

  const searchLocations = (
    value: string,
    locationData: TQSLocation[] = locations,
  ) => {
    setSearchInput(value);
    dispatch(setSearch(value));
  };

  const navigateToOrderPage = () => {
    window.open(environment.ordersUrl, "_blank");
  };

  const removeSelectedOem = (oemId: number) => {
    dispatch(setSelectedOems(selectedOems.filter((o) => o.id !== oemId)));
  };

  return (
    <div className="max-w[260px] relative flex h-[100%] w-full flex-col justify-between overflow-visible rounded-[8px] border-[1px] border-[#E5E5E5] bg-white p-[16px]">
      <div className="relative h-[100%]">
        <div className="flex flex-col pb-[8px]">
          <span className="text-[20px] font-semibold text-[#212322]">
            Service Locations
          </span>
          <span className="text-[14px] font-semibold text-[grey]">
            All locations where you have liaison coverage
          </span>
        </div>

        <div className="relative flex flex-row items-center gap-x-[6px]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400"
          >
            <path
              fillRule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clipRule="evenodd"
            />
          </svg>
          <input
            className="input input-bordered h-[36px] w-[100%] rounded-[4px] bg-[#E5E5E5] pl-[30px] text-[14px] placeholder-[#8e8e93]"
            placeholder="Search"
            onChange={(e) => searchLocations(e.target.value)}
          />
          <button
            className="indicator flex h-[36px] w-[36px] min-w-[36px] items-center justify-center rounded-[4px] border-[1px] border-[#E5E5E5]"
            onClick={() =>
              setOpen(isOpen(modalIdentifier) ? "" : modalIdentifier)
            }
          >
            {/* <span className="indicator-item flex h-[15px] w-[15px] items-center justify-center rounded-[50%] bg-[#fedd00] text-[10px] font-bold text-[#212322]">
              {selectedOems.length}
            </span> */}
            <FontAwesomeIcon icon={faFilter} />
          </button>

          {isOpen(modalIdentifier) && (
            <div className="absolute left-[calc(100%-80px)] top-[40px] z-[999999999999] w-[250px] rounded-md border-[1px] border-[#E5E5E5] bg-[white] p-[12px] shadow-lg">
              <div className="flex w-[100%] flex-col">
                <div className="mb-[8px] flex items-center justify-between border-b-[1px] pb-[4px]">
                  <span className="text-[14px] font-semibold text-[#212322]">
                    Select OEMS
                  </span>
                  <FontAwesomeIcon
                    className="cursor-pointer"
                    icon={faClose}
                    onClick={() => setOpen("")}
                  />
                </div>
                {oem.map((o, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      updateSelectedOems(
                        {
                          target: {
                            checked: !selectedOems.some((s) => s.id === o.id),
                          } as any,
                        } as any,
                        o,
                      )
                    }
                    className="flex w-[100%] cursor-pointer flex-row items-center gap-x-[6px] pb-[3px] hover:bg-gray-50"
                  >
                    <input
                      onChange={(e) => updateSelectedOems(e, o)}
                      checked={selectedOems.some((s) => s.id === o.id)}
                      type="checkbox"
                      className="checkbox max-h-[16px] max-w-[16px] rounded-[50%] border-[#212322] [--chkbg:#fedd00] [--chkfg:black]"
                    />
                    <span className="text-[14px] font-semibold text-[#212322]">
                      {o.name}
                    </span>
                  </div>
                ))}

                <div className="flex w-[100%] justify-end">
                  <button
                    onClick={() => resetFilter()}
                    className="h-[26px] w-[100px] rounded-[4px] border-0 bg-[#212322] text-[14px] font-medium text-[#ffffff]"
                  >
                    Clear Filter
                  </button>
                </div>
              </div>
            </div>
          )}

          <button
            data-tooltip-id="order-tooltip"
            data-tooltip-content="Add more coverage"
            className="flex h-[36px] w-[36px] min-w-[36px] items-center justify-center rounded-[4px] border-[1px] border-[#212322] bg-[#212322] text-[#e5e5e5]"
            onClick={() => navigateToOrderPage()}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>

          <Tooltip id="order-tooltip" />
        </div>
        {/* <div className="mt-2 flex flex-wrap gap-2">
          {selectedOems.map((oem) => (
            <div
              key={oem.id}
              className="flex items-center space-x-2 rounded bg-[#E5E5E5] p-1"
            >
              <span className="text-xs">{oem.name}</span>
              <button
                onClick={() => removeSelectedOem(oem.id)}
                className="text-red-500"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div> */}
        <div className="mt-[8px] flex h-[calc(100%-170px)] w-[100%] flex-col gap-y-[8px] overflow-y-auto max-[1115px]:h-[calc(100%-220px)] max-[1075px]:h-[calc(100%-260px)]">
          {(filteredLocations.length === 0 || isLoading) && (
            <div className="pt-2 text-center">
              <span className="loading loading-spinner loading-lg text-tqsYellow"></span>
            </div>
          )}
          {!isLoading &&
            filteredLocations.map((l, index) => (
              <LocationCard
                key={index}
                index={index}
                location={l}
                setSelectedLocation={props.setSelectedLocation}
                selectedLocation={props.selectedLocation}
              />
            ))}
        </div>

        <div
          className="mt-4 flex cursor-pointer items-center justify-center overflow-hidden rounded-[4px] border-[1px] border-dashed border-[#E5E5E5] bg-[#F7F7F7] p-[8px] text-[14px] text-[#00000060]"
          onClick={() => navigateToOrderPage()}
        >
          <span>Add more coverage +</span>
        </div>
      </div>
    </div>
  );
}
